import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { fetchMediaObjectUrl } from '@src/services/media'

/**
 * Drop-in for <img> whose src is a PROTECTED media path served by the
 * auth-gated /uploads proxy (KTP/selfie/consent docs/logos/avatars). A plain
 * <img> can't send the Authorization header, so we fetch the bytes as a blob
 * and render an object URL.
 *
 * Renders `fallback` (default: nothing) while loading or on error — keep the
 * caller's own "no image" branch (initials, placeholder) around the element as
 * before; this only swaps the <img> itself.
 */
export default function AuthImage({ src, alt, fallback, ...imgProps }) {
  const [objectUrl, setObjectUrl] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let active = true
    let current = null
    setObjectUrl(null)
    setFailed(false)
    if (!src) return undefined
    const controller = new AbortController()
    fetchMediaObjectUrl(src, controller.signal)
      .then((url) => {
        if (!active) {
          if (url) URL.revokeObjectURL(url)
          return
        }
        current = url
        setObjectUrl(url)
      })
      .catch((err) => {
        if (active && err?.name !== 'AbortError') setFailed(true)
      })
    return () => {
      active = false
      controller.abort()
      if (current) URL.revokeObjectURL(current)
    }
  }, [src])

  if (!src || failed || !objectUrl) return fallback ?? null
  return <img src={objectUrl} alt={alt} {...imgProps} />
}

AuthImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  fallback: PropTypes.node,
}

AuthImage.defaultProps = {
  src: null,
  alt: '',
  fallback: null,
}
